import * as React from 'react';
import { ElementType, ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  TableCellBaseProps,
  Typography,
} from '@mui/material';

import { ContentBox } from 'components/ContentBox';

import { TablePaginationActions } from './table-padination';
import styles from './styles.module.scss';
import { MagnifyingGlassCustom } from '../../Loaders/MagnifyingGlass';

export type Column = {
  title: string;
  component?: ReactNode;
};

export type Cell = {
  key: string;
  sort: number;
  component: ElementType<TableCellBaseProps> | undefined;
  scope: string | undefined;
  isBold?: boolean | undefined;
  data: ReactNode;
  width?: string;
};

export type Row = {
  id: string;
  key: string;
  cells: Cell[];
};

export type DashboardTableProps = {
  columns: Column[];
  rows: Row[];
  isSearch?: boolean;
  rowHeight?: number;
  withPagination?: boolean;
  onClickRow?: (id: string) => void;
  defaultRowsPerPage?: number;
};

export const createData = (
  ...args: {
    key: string;
    id?: string;
    isBold?: boolean;
    element: ReactNode;
    width?: string;
  }[]
): Cell[] => {
  return args.map((data, index) => {
    return {
      key: data.key,
      sort: index,
      isBold: data.isBold,
      component: !index ? 'th' : undefined,
      scope: !index ? 'row' : undefined,
      data: data.element,
      width: data.width,
    };
  });
};

const rowsPerPageEmpty = 3;

export const DashboardTable = ({
  columns,
  rows,
  isSearch,
  rowHeight = 65,
  withPagination = true,
  onClickRow,
  defaultRowsPerPage = 10,
}: DashboardTableProps) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    withPagination ? defaultRowsPerPage : -1
  );

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <TableContainer component={ContentBox}>
      <Table
        sx={{ minWidth: 900 }}
        aria-label="simple table"
        className={styles.root}
      >
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                align={index === columns.length - 1 ? 'right' : 'left'}
                key={column.title}
              >
                {column.component ? column.component : column.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!isSearch &&
            (rowsPerPage > 0
              ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : rows
            ).map((row) => (
              <TableRow
                onClick={() => {
                  onClickRow && onClickRow(row.key);
                }}
                key={row.key}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: onClickRow ? 'pointer' : 'unset',
                }}
              >
                {row.cells.map((cell, index) => (
                  <TableCell
                    align={index === columns.length - 1 ? 'right' : 'left'}
                    key={cell.key}
                    component={cell.component}
                    scope={cell.scope}
                  >
                    {cell.isBold ? <b>{cell.data}</b> : cell.data}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {isSearch && (
            <TableRow style={{ height: rowHeight * rowsPerPage }}>
              <TableCell colSpan={6} align="center">
                <MagnifyingGlassCustom />
              </TableCell>
            </TableRow>
          )}
          {!isSearch && !rows.length && (
            <TableRow style={{ height: rowHeight * rowsPerPageEmpty }}>
              <TableCell colSpan={6} align="center">
                <Typography className={styles.noData}>
                  No pending requests
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!isSearch && emptyRows > 0 && (
            <TableRow style={{ height: rowHeight * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        {withPagination && (
          <TableFooter>
            <TableRow>
              <TablePagination
                className={styles.pagination}
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                colSpan={5}
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  className: styles.paginationLabel,
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};
